import { usePhoneLayout } from "../lib/PhoneLayout";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useStore } from "../store/StoreContext";
import { img } from "../lib/format";
import CatalogueDetails from "./CatalogueDetails";
import ExportPrice from "./ExportPrice";
import { useReducedData } from "./WordReveal";
import { productPresentation } from "../lib/productPresentation";

export default function ProductCard({ product, index = 0, variant = "cutout", className = "", compact = false }) {
  const { wishlist, toggleWishlist, setQuickView } = useStore();
  const wished = wishlist.includes(product.slug);
  const presentation = productPresentation(product);
  const cutout = img(presentation.images[0]);
  const lifestyle = img(presentation.lifestyle[0] || presentation.images[0]);
  const lifestyleFirst = variant === "lifestyle";
  const primary = lifestyleFirst ? lifestyle : cutout;
  const alternate = lifestyleFirst ? cutout : lifestyle;
  const phone = usePhoneLayout();
  const reducedMotion = useReducedMotion();
  const reduced = phone || reducedMotion;
  const reducedData = useReducedData();
  const [alternateRequested, setAlternateRequested] = useState(false);
  const [alternateLoaded, setAlternateLoaded] = useState("");
  const [hovered, setHovered] = useState(false);
  const canSwap = Boolean(alternate && alternate !== primary && !reducedData);
  const showAlternate = canSwap && hovered && alternateLoaded === alternate;

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={reduced || reducedData ? false : { opacity: 0, y: 24 }}
      whileInView={reduced || reducedData ? undefined : { opacity: 1, y: 0 }}
      animate={reduced || reducedData ? { opacity: 1, y: 0 } : undefined}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: reduced || reducedData ? 0 : 0.6, delay: reduced || reducedData ? 0 : (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className={`group text-center ${className}`}
      onPointerEnter={(event) => {
        if (canSwap && (event.pointerType === "mouse" || event.pointerType === "pen") && window.matchMedia?.("(hover: hover)").matches) {
          setAlternateRequested(true);
          setHovered(true);
        }
      }}
      onPointerLeave={() => setHovered(false)}
    >
      <div className={`relative overflow-hidden rounded-[22px] ${lifestyleFirst ? "aspect-[3/4] bg-beige" : "aspect-square bg-white"}`}>
        <Link to={`/product/${product.slug}`} data-testid={`product-link-${product.slug}`} className="absolute inset-0 block">
          <img
            src={primary?.includes("/christmas-2027/") ? primary.replace(/\.webp$/, "-card.webp") : primary}
            alt={presentation.alt}
            loading="lazy"
            decoding="async"
            style={{ opacity: showAlternate ? 0 : 1 }}
            className="absolute inset-0 w-full h-full object-contain p-6 cutout-shadow transition-opacity duration-700 motion-reduce:transition-none"
          />
          {canSwap && alternateRequested && <img
            src={alternate}
            alt={`${presentation.alt} — additional catalogue image`}
            aria-hidden={!showAlternate}
            loading="eager"
            decoding="async"
            onLoad={() => setAlternateLoaded(alternate)}
            style={{ opacity: showAlternate ? 1 : 0 }}
            className="absolute inset-0 w-full h-full object-contain p-6 cutout-shadow transition-opacity duration-700 motion-reduce:transition-none"
          />}
        </Link>

        <button
          data-testid={`product-wishlist-toggle-${product.slug}`}
          onClick={() => toggleWishlist(product)}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          aria-pressed={wished}
          className="absolute top-3 right-3 w-11 h-11 rounded-full bg-white/90 shadow flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity duration-300 hover:text-brass"
        >
          <Heart className={`w-4 h-4 ${wished ? "fill-brass text-brass" : ""}`} strokeWidth={1.5} />
        </button>

      </div>

      {!compact && <button
        data-testid={`product-quick-view-${product.slug}`}
        onClick={() => setQuickView(product.slug)}
        className="pill-dark min-h-11 mt-4 px-4 py-2 text-[11px] font-normal lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100 focus-visible:opacity-100 lg:translate-y-1 lg:group-hover:translate-y-0 transition-[opacity,transform,background-color] duration-300"
      >
        Quick view
      </button>}

      <Link to={`/product/${product.slug}`} className="block mt-3">
        <h3 className="text-[16px] sm:text-[18px] font-light text-ink hover:text-brass transition-colors duration-200">{product.name}</h3>
      </Link>
      {compact ? <p className="mt-2 text-sm text-mocha">{product.sku || product.item_codes} · MOQ 500 pieces</p> : <>
      <p className="mt-1 text-[10px] text-mocha tracking-wide">
        Crafted by <span className="brand-name underline decoration-mocha/50 underline-offset-2">S H &amp; SONS ®</span>
      </p>
      <p className="mt-2 text-[12px] text-mocha line-clamp-2">{presentation.shortDescription || "MOQ 500 pieces"}</p>
      <CatalogueDetails product={product} className="mt-2" />
      </>}
      <ExportPrice product={product} />
      {!compact && <p className={phone ? "mt-1.5 text-xs text-mocha" : "mt-1.5 text-[10px] text-mocha"}>{product.collections?.includes("christmas-2027") ? "Christmas Collection 2027" : "Design preview · selection pending"}</p>}
      {product.is_new && <p className="mt-1.5 text-[10px] uppercase tracking-[0.25em] text-brass">New Arrival</p>}
    </motion.article>
  );
}
